import type { SpecType } from '@local/lib';

import { Badge, Button, Card, Combobox, Icon, Spinner, Tooltip } from '@salesforce/design-system-react';
import { useState } from 'react';

import type { QualityAdjustmentTemplate } from '../../templates/templateTypes';

import { IconCategory } from '../../../shared/types/Enum';
import { showErrorNotification, showSuccessNotification } from '../../../shared/utils/notifications';
import { ConfirmationModal } from '../../templates/components/ConfirmationModal';
import { TemplateEditorContent } from '../../templates/components/TemplateEditorModal';
import { usePermissions } from '../../templates/hooks/usePermissions';
import { useTemplateActions, useTemplates } from '../../templates/hooks/useTemplates';
import { ADJUSTMENT_CATEGORY_LABEL } from '../../templates/templateTypes';
import { getFormulaAdjustmentCategory, SPEC_TYPES } from '../qualityFormulaMetadata';

const ALL_SPECS_FILTER_VALUE = 'ALL';

const getSpecLabel = (key: SpecType): string => SPEC_TYPES.find(spec => spec.key === key)?.label ?? key;

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

interface TemplateManagerRowProps {
  canManage: boolean;
  onDelete: (template: QualityAdjustmentTemplate) => void;
  onDuplicate: (template: QualityAdjustmentTemplate) => void;
  onEdit: (template: QualityAdjustmentTemplate) => void;
  template: QualityAdjustmentTemplate;
}

const TemplateManagerRow: React.FC<TemplateManagerRowProps> = ({
  canManage,
  onDelete,
  onDuplicate,
  onEdit,
  template,
}) => {
  const adjustmentCategory = getFormulaAdjustmentCategory(template.config.formulaType);

  return (
    <tr>
      <td>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-1)' }}>
          <span className="slds-text-body_regular" style={{ fontWeight: 'var(--slds-g-font-weight-7)' }}>
            {template.name}
          </span>
          {template.description ? (
            <span className="slds-text-body_small slds-text-color_weak slds-truncate" style={{ display: 'block' }}>
              {template.description}
            </span>
          ) : null}
        </div>
      </td>
      <td>
        <Badge color="light" content={getSpecLabel(template.specType)} />
      </td>
      <td>
        <span className="slds-text-body_small">{ADJUSTMENT_CATEGORY_LABEL[adjustmentCategory]}</span>
      </td>
      <td>
        <span className="slds-text-body_small slds-text-color_weak">{formatDate(template.updatedAt)}</span>
      </td>
      <td className="slds-text-align_center">
        <div
          className="slds-grid slds-grid_align-end slds-grid_vertical-align-center slds-nowrap"
          style={{ gap: 'var(--slds-g-spacing-1)' }}
        >
          <Tooltip align="right" content={canManage ? 'Edit template' : 'View template'}>
            <Button
              assistiveText={{ icon: canManage ? 'Edit template' : 'View template' }}
              iconCategory={IconCategory.Utility}
              iconName={canManage ? 'edit' : 'preview'}
              onClick={() => {
                onEdit(template);
              }}
              title={canManage ? 'Edit template' : 'View template'}
              variant="icon"
            />
          </Tooltip>
          {canManage ? (
            <>
              <Tooltip align="right" content="Duplicate template">
                <Button
                  assistiveText={{ icon: 'Duplicate template' }}
                  iconCategory={IconCategory.Utility}
                  iconName="copy"
                  onClick={() => {
                    onDuplicate(template);
                  }}
                  title="Duplicate template"
                  variant="icon"
                />
              </Tooltip>
              <Tooltip align="right" content="Delete template">
                <Button
                  assistiveText={{ icon: 'Delete template' }}
                  className="slds-button_icon-error"
                  iconCategory={IconCategory.Utility}
                  iconName="delete"
                  onClick={() => {
                    onDelete(template);
                  }}
                  title="Delete template"
                  variant="icon"
                />
              </Tooltip>
            </>
          ) : null}
        </div>
      </td>
    </tr>
  );
};

/**
 * The Quality Adjustment workbench's inline Template Manager — the same browse/duplicate/delete
 * behavior as the standalone `/templates` page (`TemplateManagerPage.tsx`), but rendered in the
 * workbench's own content area (the same slot the Playground occupies, see `QualitySpecsWorkbench`)
 * instead of navigating away. Create/Edit swap `TemplateEditorContent` directly into this widget's
 * own card in place of the list (rather than opening a separate floating dialog or the routed
 * `/templates/new`/`/templates/:id/edit` pages), so the current contract/line item is never left
 * and the editor stays visually anchored to the Manage Templates widget itself.
 */
export const TemplateManagerPanel: React.FC = () => {
  const { data: templates, isLoading } = useTemplates();
  const { duplicateTemplate, removeTemplate } = useTemplateActions();
  const { canManageTemplates } = usePermissions();
  const [specFilter, setSpecFilter] = useState<string>(ALL_SPECS_FILTER_VALUE);
  const [pendingDelete, setPendingDelete] = useState<null | QualityAdjustmentTemplate>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editorState, setEditorState] = useState<{ isOpen: boolean; templateId: null | string }>({
    isOpen: false,
    templateId: null,
  });

  const selectedSpecType =
    specFilter === ALL_SPECS_FILTER_VALUE
      ? null
      : // Pre-existing cross-package enum-identity lint quirk — see api/pricingEngineApi.ts.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
        (specFilter as SpecType);
  const specFilterOptions = [
    { id: ALL_SPECS_FILTER_VALUE, label: 'All Quality Specifications' },
    ...SPEC_TYPES.map(spec => ({ id: spec.key, label: spec.label })),
  ];
  const selectedSpecFilterOption = specFilterOptions.find(option => option.id === specFilter);
  const visibleTemplates = (templates ?? []).filter(
    template => selectedSpecType === null || template.specType === selectedSpecType,
  );

  const openEditor = (templateId: null | string) => {
    setEditorState({ isOpen: true, templateId });
  };
  const closeEditor = () => {
    setEditorState({ isOpen: false, templateId: null });
  };

  const handleDuplicate = async (template: QualityAdjustmentTemplate) => {
    try {
      const duplicate = await duplicateTemplate(template.id);
      showSuccessNotification(`Duplicated "${template.name}".`);
      openEditor(duplicate.id);
    } catch (error) {
      showErrorNotification(error, 'Unable to duplicate template');
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      await removeTemplate(pendingDelete.id);
      showSuccessNotification(`Deleted "${pendingDelete.name}".`);
    } catch (error) {
      showErrorNotification(error, 'Unable to delete template');
    } finally {
      setIsDeleting(false);
      setPendingDelete(null);
    }
  };

  let templatesBody: React.ReactNode;
  if (isLoading) {
    templatesBody = (
      <div className="slds-grid slds-grid_align-center slds-p-around_x-large">
        <Spinner hasContainer={false} />
      </div>
    );
  } else if (visibleTemplates.length === 0) {
    templatesBody = (
      <div className="slds-box slds-text-align_center" style={{ borderStyle: 'dashed' }}>
        <p className="slds-text-body_small slds-text-color_weak">
          {selectedSpecType === null ? 'No templates yet.' : `No ${getSpecLabel(selectedSpecType)} templates yet.`}{' '}
          {canManageTemplates ? 'Click "Create Template" to build one.' : 'Check back later.'}
        </p>
      </div>
    );
  } else {
    templatesBody = (
      <div className="slds-scrollable_x">
        <table className="slds-table slds-table_bordered slds-table_cell-buffer">
          <thead>
            <tr className="slds-line-height_reset">
              <th scope="col">
                <div className="slds-truncate">Template Name</div>
              </th>
              <th scope="col">
                <div className="slds-truncate">Quality Specification</div>
              </th>
              <th scope="col">
                <div className="slds-truncate">Adjustment Type</div>
              </th>
              <th scope="col">
                <div className="slds-truncate">Last Modified</div>
              </th>
              <th className="slds-text-align_center" scope="col">
                <div className="slds-truncate">Action</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleTemplates.map(template => (
              <TemplateManagerRow
                canManage={canManageTemplates}
                key={template.id}
                onDelete={setPendingDelete}
                onDuplicate={template => {
                  void handleDuplicate(template);
                }}
                onEdit={template => {
                  openEditor(template.id);
                }}
                template={template}
              />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <section aria-label="Manage Templates" className="qfw-playground-surface">
      <Card
        className="qfw-playground"
        heading={
          <span className="qfw-playground-heading">
            <Icon
              category={IconCategory.Utility}
              name="recipe"
              size="small"
              style={{ fill: 'var(--slds-g-color-accent-1)' }}
            />
            <span>Manage Templates</span>
          </span>
        }
      >
        <hr className="qfw-divider slds-p-bottom_small" />
        {editorState.isOpen ? (
          <TemplateEditorContent compact onClose={closeEditor} templateId={editorState.templateId} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-3)' }}>
            <div
              className="slds-grid slds-grid_align-spread slds-grid_vertical-align-end slds-wrap"
              style={{ gap: 'var(--slds-g-spacing-3)' }}
            >
              <div style={{ minWidth: '14rem' }}>
                <Combobox
                  assistiveText={{ label: 'Filter by Quality Specification' }}
                  className="qfw-template-filter-combobox"
                  classNameMenu="qfw-template-filter-menu"
                  events={{
                    onSelect: (_event, data) => {
                      const selectedId = data.selection[0]?.id as string | undefined;
                      setSpecFilter(selectedId ?? ALL_SPECS_FILTER_VALUE);
                    },
                  }}
                  id="template-manager-panel-spec-filter"
                  labels={{ placeholder: 'Filter by Quality Specification' }}
                  options={specFilterOptions}
                  predefinedOptionsOnly
                  selection={selectedSpecFilterOption ? [selectedSpecFilterOption] : []}
                  variant="readonly"
                />
              </div>
              {canManageTemplates ? (
                <Button
                  iconCategory={IconCategory.Utility}
                  iconName="add"
                  iconPosition="left"
                  label="Create Template"
                  onClick={() => {
                    openEditor(null);
                  }}
                  variant="brand"
                />
              ) : null}
            </div>

            {templatesBody}
          </div>
        )}
      </Card>

      <ConfirmationModal
        confirmLoading={isDeleting}
        confirmText="Delete"
        onCancel={() => {
          setPendingDelete(null);
        }}
        onConfirm={() => {
          void handleConfirmDelete();
        }}
        opened={pendingDelete !== null}
        title="Delete this template?"
      >
        <p className="slds-text-body_small">
          Deleting “{pendingDelete?.name}” can’t be undone. Any contract that already loaded its values is unaffected.
        </p>
      </ConfirmationModal>
    </section>
  );
};
